// IndexedDB persistence for the form-draft autosave store — see
// docs/specs/autosave-indexeddb-migration.md. The whole AutosaveStorage
// object lives under ONE key: the provider's semantics (merge, tombstones,
// size accounting) all operate on full-state snapshots, so a single-key
// store keeps this a pure persistence swap. localStorage's ~5 MB quota was
// the structural ceiling (a Words & Pictures draft is ~1.5-2 MB of
// inline-image JSON); IndexedDB's origin quota is effectively unbounded
// for our purposes.
//
// The connection layer (singleton, open-hang watchdog, the
// `onversionchange → close()` upgrade-deadlock fix, commit-gated writes) is
// the shared machinery in `libTs/storage/sharedIdb.ts`.
//
// DB_NAME / STORE / KEY / version are LIVE USER DATA: live drafts sit at
// exactly these coordinates. Changing any of them strands every caseworker's
// unsaved work. `__tests__/autosaveSchemaLock.test.ts` pins them as literals.
//
// `clearAllAutosaveStorage` is a LOGICAL delete, and the UI must not promise
// more than that (sgdev card `ymp`): it removes the stored value so no draft
// is reachable through this app, and ASKS the browser to drop the database
// file. Treat the value delete as the guarantee and the `deleteDatabase` as
// opportunistic — another tab blocks it, and this tab's own persist effect
// can re-create an EMPTY database around it. It is NOT an erasure guarantee
// either way — it says nothing about what remains on disk in freed pages,
// OS-level backups, or swap.

import { createSharedIdbConnection } from '../../../libTs/storage/sharedIdb';

const DB_NAME = 'hspp-form-autosaves';
const DB_VERSION = 1;
const STORE = 'kv';
const KEY = 'autosaves';

// Mirrors AutosaveContext's AutosaveStorage without importing it (the
// context imports this module; keep the dependency one-way).
export type StoredAutosaveStorage = {
  autosaves: unknown[];
  lastTombstoneClearTime?: number;
};

const connection = createSharedIdbConnection({
  dbName: DB_NAME,
  version: DB_VERSION,
  logLabel: 'AutosaveDb',
  // AutosaveContext renders children only once the open settles (`!idbMode ||
  // idbReady`), and only a REJECTED open flips it into localStorage mode. A
  // blocked open left pending would blank the app, so fail fast instead.
  blockedBehavior: 'reject',
  onUpgrade: (db) => {
    if (!db.objectStoreNames.contains(STORE)) {
      db.createObjectStore(STORE);
    }
  },
});

export async function loadAutosaveStorage(): Promise<StoredAutosaveStorage | null> {
  const stored = await connection.withRead<StoredAutosaveStorage | undefined>(
    STORE,
    (store) => store.get(KEY)
  );
  return stored ?? null;
}

export async function saveAutosaveStorage(
  value: StoredAutosaveStorage
): Promise<void> {
  await connection.withWrite(STORE, (store) => store.put(value, KEY));
}

/**
 * Remove every draft this origin has stored, for an explicit caseworker-
 * initiated disposal (sgdev card `ymp`). Never call this from a lifecycle
 * event — not logout, not session expiry.
 *
 * Order matters. The single value is deleted FIRST, inside a commit-gated
 * write transaction: that lands even while sibling tabs hold open connections,
 * so the content is gone from the store whatever happens next. Only then is
 * the connection dropped and `deleteDatabase` attempted, and that attempt is
 * best-effort — another tab's live connection blocks it, and a blocked or
 * failed delete must not hang or fail the disposal, because the content has
 * already been removed. In that case the database file survives with an empty
 * store, which is also what a `deleteDatabase` that raced this tab's own
 * persist effect leaves behind. The removal of the CONTENT is the promise
 * here; the disappearance of the file is not.
 *
 * A browser with no IndexedDB at all (the localStorage-fallback mode the
 * provider supports) returns successfully without opening anything: there is
 * nothing stored here to remove, and opening a connection just to find that
 * out would reject and report a failed disposal.
 */
export async function clearAllAutosaveStorage(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  await connection.withWrite(STORE, (store) => store.delete(KEY));
  connection.close();

  await new Promise<void>((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.deleteDatabase(DB_NAME);
    } catch (err) {
      console.warn(
        '[AutosaveDb] deleteDatabase threw; the stored drafts value was already removed',
        err
      );
      resolve();
      return;
    }
    request.onsuccess = () => resolve();
    request.onblocked = () => {
      console.warn(
        '[AutosaveDb] deleteDatabase blocked by another tab; the stored drafts value was already removed'
      );
      resolve();
    };
    request.onerror = () => {
      console.warn(
        '[AutosaveDb] deleteDatabase failed; the stored drafts value was already removed',
        request.error
      );
      resolve();
    };
  });
}

/** Drop the singleton connection (tests swapping the IDB factory need this). */
export const closeAutosaveDb = connection.close;
